import * as React from 'react';
import { Link, useLocation, useRoute } from 'wouter';
import {
  ScanLine,
  ClipboardList,
  FileText,
  Package,
  MoreHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { useAuth, ALL_FEATURES_ENABLED, type FeatureKey, type SiteFeatures } from '@/auth/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { MobileMoreSheet } from './MobileMoreSheet';

interface Tab {
  label: string;
  icon: LucideIcon;
  feature: FeatureKey;
  /** Built from currentSlug at render time. */
  hrefFor: (slug: string) => string;
  /** Active match (exact or descendant). */
  matches: (slug: string, location: string) => boolean;
}

const TABS: Tab[] = [
  {
    label: 'Scan',
    icon: ScanLine,
    feature: 'scan',
    hrefFor: (s) => `/sites/${s}/scan`,
    matches: (s, loc) => loc === `/sites/${s}/scan`,
  },
  {
    label: 'Rounding',
    icon: ClipboardList,
    feature: 'rounding',
    hrefFor: (s) => `/sites/${s}/rounding`,
    matches: (s, loc) => loc.startsWith(`/sites/${s}/rounding`),
  },
  {
    label: 'DAR',
    icon: FileText,
    feature: 'dar',
    hrefFor: (s) => `/sites/${s}/dar`,
    matches: (s, loc) => loc === `/sites/${s}/dar`,
  },
  {
    label: 'Storage',
    icon: Package,
    feature: 'storage',
    hrefFor: (s) => `/sites/${s}/storage`,
    matches: (s, loc) =>
      loc === `/sites/${s}/storage` ||
      loc.startsWith(`/sites/${s}/storage/discharge`) ||
      loc === `/sites/${s}/storage/temp`,
  },
];

export function MobileTabBar() {
  const [location] = useLocation();
  const { slug, features } = useCurrentSiteContext();
  const [moreOpen, setMoreOpen] = React.useState(false);

  // Hide on the unauth login screen, the scan camera flow, etc — but for now
  // we always render below md. Spec says officers should always have it.
  if (!slug) return null;

  const visibleTabs = TABS.filter((t) => features[t.feature]);

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border"
        aria-label="Primary"
      >
        <ul className="flex items-stretch justify-around h-14 pb-[env(safe-area-inset-bottom)]">
          {visibleTabs.map((tab) => {
            const href = tab.hrefFor(slug);
            const active = tab.matches(slug, location);
            const Icon = tab.icon;
            return (
              <li key={tab.label} className="flex-1">
                <Link
                  href={href}
                  className={cn(
                    'flex flex-col items-center justify-center h-full gap-0.5 text-[10px] transition-colors',
                    active ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {tab.label}
                </Link>
              </li>
            );
          })}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className="flex w-full h-full flex-col items-center justify-center gap-0.5 text-[10px] text-muted-foreground"
            >
              <MoreHorizontal className="h-5 w-5" />
              More
            </button>
          </li>
        </ul>
      </nav>
      <MobileMoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        currentSlug={slug}
      />
    </>
  );
}

interface AdminSiteRow {
  slug: string;
  active: boolean;
  featureScan: boolean;
  featureTours: boolean;
  featureRounding: boolean;
  featureDar: boolean;
  featureStorage: boolean;
  featureCensus: boolean;
  featureAlerts: boolean;
}

/**
 * Picks the slug + feature flags for the bottom tabs.
 *
 * 1. If the current URL is /sites/:slug/..., use that slug.
 * 2. Otherwise fall back to the first accessible site (siteAccess for non-
 *    admin; first active site for Global.Admin).
 * 3. If neither, slug is null and the tab bar hides itself.
 *
 * `features` defaults to all-true if we can't resolve them — that matches the
 * historical behavior where every tab was always shown.
 */
function useCurrentSiteContext(): { slug: string | null; features: SiteFeatures } {
  const { me, isGlobalAdmin } = useAuth();
  const [match, params] = useRoute<{ slug: string }>('/sites/:slug/:rest*');

  const adminSitesQuery = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => api.get<AdminSiteRow[]>('/api/admin/sites'),
    enabled: isGlobalAdmin,
  });

  const adminRows = adminSitesQuery.data ?? [];

  function featuresForAdmin(slug: string): SiteFeatures {
    const r = adminRows.find((row) => row.slug === slug);
    if (!r) return ALL_FEATURES_ENABLED;
    return {
      scan: r.featureScan,
      tours: r.featureTours,
      rounding: r.featureRounding,
      dar: r.featureDar,
      storage: r.featureStorage,
      census: r.featureCensus,
      alerts: r.featureAlerts,
    };
  }

  function resolveFor(slug: string): SiteFeatures {
    if (isGlobalAdmin) return featuresForAdmin(slug);
    const entry = me?.siteAccess.find((a) => a.siteSlug === slug);
    return entry?.features ?? ALL_FEATURES_ENABLED;
  }

  if (match && params?.slug) {
    return { slug: params.slug, features: resolveFor(params.slug) };
  }
  if (me?.siteAccess[0]) {
    const a = me.siteAccess[0];
    return { slug: a.siteSlug, features: a.features ?? ALL_FEATURES_ENABLED };
  }
  if (isGlobalAdmin) {
    const firstActive = adminRows.find((s) => s.active);
    if (firstActive) {
      return { slug: firstActive.slug, features: featuresForAdmin(firstActive.slug) };
    }
  }
  return { slug: null, features: ALL_FEATURES_ENABLED };
}
